import { useLazyQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo } from 'react';
import { useLatest, usePrevious } from 'react-use';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import { WorkerStatusTypesLookup } from '@eluve/graphql-types';
import { useIsEluveAdmin } from '@eluve/session-helpers';

import { useAppointmentContext } from './appointment.context';
import {
  getCurrentSummary,
  getCurrentSummaryForEluveAdmin,
} from './operations';
import { useAppointmentActivity } from './useAppointmentActivity';
import { SUMMARY_TAB, useAppointmentTabNav } from './useAppointmentTabNav';

interface UseListenForSummaryOptions {
  onCompleted?: () => void;
}

export const useListenForSummary = (options?: UseListenForSummaryOptions) => {
  const { onCompleted } = options || {};
  const onCompletedRef = useLatest(onCompleted);
  const { latestSummaryStatus, isSummarizationInProgress } =
    useAppointmentActivity();
  const { tenantId, appointmentId } = useAppointmentContext();
  const { setTab } = useAppointmentTabNav();
  const isEluveAdmin = useIsEluveAdmin();
  const summaryQuery = useMemo(
    () => (isEluveAdmin ? getCurrentSummaryForEluveAdmin : getCurrentSummary),
    [isEluveAdmin],
  );

  const [getLatest] = useLazyQuery(summaryQuery);

  const updateSummary = useCallback(async () => {
    await getLatest({
      variables: { tenantId, appointmentId },
      context: isEluveAdmin ? eluveAdminHasuraContext : undefined,
    });
    setTab(SUMMARY_TAB);

    if (latestSummaryStatus === WorkerStatusTypesLookup.COMPLETED) {
      onCompletedRef.current?.();
    }
  }, [getLatest, setTab, latestSummaryStatus, onCompletedRef, isEluveAdmin]);

  const prevLatestSummaryStatus = usePrevious(latestSummaryStatus);
  const wasSummarizationInProgress = usePrevious(isSummarizationInProgress);

  useEffect(() => {
    const startedGenerating =
      isSummarizationInProgress && !wasSummarizationInProgress;

    // Handle edge case where audio is empty or too short, the summarization fails immediately from PENDING to FAILED state.
    const finishedGenerating =
      (latestSummaryStatus === WorkerStatusTypesLookup.COMPLETED ||
        latestSummaryStatus === WorkerStatusTypesLookup.FAILED) &&
      (prevLatestSummaryStatus === WorkerStatusTypesLookup.IN_PROGRESS ||
        prevLatestSummaryStatus ===
          WorkerStatusTypesLookup.PENDING_DATA_UPLOAD);

    if (startedGenerating || finishedGenerating) {
      updateSummary();
    }
  }, [
    isSummarizationInProgress,
    wasSummarizationInProgress,
    latestSummaryStatus,
    prevLatestSummaryStatus,
    updateSummary,
  ]);
};
